/**
 * @typedef ProductExceptionObj
 * @property {string} [min_date]
 * @property {string} [max_date]
 * @property {string[]} [available_dates]
 * @property {boolean} [unavailable_shipping]
 * @property {string} [city_exclusive]
 */

/**
 * @typedef ZoneObj
 * @property {number} id
 * @property {string} name
 * @property {string} state
 * @property {string} color
 * @property {number} sameDayDeliveryCharge
 * @property {number} deliveryCharge
 * @property {string} description
 * @property {boolean} isPickup
 * @property {number} ShopId
 */

/**
 * @typedef ShopifyLocationObj
 * @property {string} name
 * @property {string} city
 * @property {string} address1
 */

/**
 * @typedef PickupZoneObjProperties
 * @property {ShopifyLocationObj} location
 *
 * @typedef {ZoneObj & PickupZoneObjProperties} PickupZoneObj
 */

/**
 * @typedef DateExceptionObj
 * @property {number} id
 * @property {string} date YYYY-MM-DD
 * @property {number} sameDayCutOff
 * @property {boolean} isAvailable
 * @property {boolean} isAvailableSameDay
 * @property {number} ZoneId
 */

class DateZonePicker {
  constructor(
    type,
    datePickerOff,
    zipCodeOff,
    pickupOff,
    preselectedData,
    productExceptions,
    inModal,
    productTitle,
    freeShippingDeal,
    cartPriceTotal,
    isMerch
  ) {
    /** @type {boolean} */
    this.datePickerOff = datePickerOff
    /** @type {boolean} */
    this.zipCodeOff = zipCodeOff
    /** @type {boolean} */
    this.pickupOff = pickupOff
    /** @type {boolean} */
    this.inModal = inModal
    /** @type {string} */
    this.productTitle = productTitle
    /** @type {boolean} */
    this.freeShippingDeal = freeShippingDeal
    /** @type {number} */
    this.cartPriceTotal = cartPriceTotal
    /** @type {boolean} */
    this.isMerch = isMerch

    /** @type {ProductExceptionObj} */
    this.productException = productExceptions

    /** @type {PreselectedDataObj} */
    this.preselectedData = preselectedData

    this.deliveryMap = type.toLowerCase() === "deliverymap"
    this.isWedding = type.toLowerCase() === "weddings"
    this.isEvent = type.toLowerCase() === "events"
    this.isSubscription = type.toLowerCase() == "subscription"
    this.dailies = type.toLowerCase() === "dailies"

    // SELF SERVE DATE EXCEPTIONS
    this.selfServeExceptions = [
      "2024-10-14",
      "2024-11-11",
      "2024-11-28",
      "2024-12-25",
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
      "2025-01-08",
      "2025-01-20",
      "2025-04-20",
      "2025-05-26",
      "2025-06-02",
      "2025-06-09",
      "2025-06-16",
      "2025-06-19",
      "2025-06-23",
      "2025-06-30",
      "2025-07-04",
      "2025-07-07",
      "2025-07-14",
      "2025-07-21",
      "2025-07-28",
      "2025-08-04",
      "2025-08-11",
      "2025-08-18",
      "2025-08-25",
      "2025-09-01",
      "2025-09-08",
      "2025-09-15",
      "2025-09-22",
      "2025-09-29",
      "2025-10-06",
      "2025-10-13",
      "2025-10-20",
      "2025-10-27",
      "2025-11-03",
      "2025-11-10",
      "2025-11-11",
      "2025-11-17",
      "2025-11-24",
      "2025-11-27",
      "2025-12-01",
      "2025-12-08",
      "2025-12-15",
      "2025-12-22",
      "2025-12-25",
      "2025-12-26",
      "2025-12-29",
    ]

    this.selfServeExceptionsByShop = {
      '1': [
        '2025-08-26',
        '2025-08-27',
        '2025-08-28',
        '2025-08-29',
        '2025-08-30',
        '2025-08-31',
        '2025-09-01',
        '2025-09-02',
        '2025-09-03'
      ],
      '2': [
        '2025-06-10',
        '2025-06-11',
        '2025-06-12',
        '2025-06-13',
        '2025-06-14',
        '2025-06-15',
        '2025-06-16',
        '2025-06-17',
        '2025-08-07',
        '2025-08-08',
        '2025-08-09',
        '2025-08-10',
        '2025-08-11'
      ]
    }

    // Initialize DatePicker.js
    if (!this.datePickerOff) {
      /** @type {import('./DatePicker').default} */
      this.datePicker = new DatePicker(
        type,
        this.isDateAvailableFunc.bind(this),
        this.highlightDateFunc.bind(this),
        this.dateSelectedFunc.bind(this),
        this.inModal,
      )
    }

    this.dateAndTime = date // dateAndTime module

    this.delivery_select = pickupOff
      ? null
      : document.querySelector("#delivery-select")
    this.pickup_select = pickupOff
      ? null
      : document.querySelector("#pickup-select")
    this.zip_input = zipCodeOff ? null : document.querySelector("#zone-input")

    this.pickup_dropdown = pickupOff
      ? null
      : document.querySelector("#studio-dropdown")
    this.pickup_input = pickupOff
      ? null
      : document.querySelector("#selected-studio")
    this.expand_pickup = pickupOff
      ? null
      : document.querySelector("#expand-studio-dropdown")
    this.edit_pickup = pickupOff
      ? null
      : document.querySelector("#edit-studio-dropdown")
    this.pickup_container = pickupOff
      ? null
      : document.getElementById("selected-studio-container")
    this.pickup_action = pickupOff
      ? null
      : document.querySelector("#studio-action")
    this.pickup_message = pickupOff
      ? null
      : document.querySelector("#studio-pickup-message")

    this.zone_form = zipCodeOff ? null : document.querySelector("#zone-form")
    this.zone_message = zipCodeOff
      ? null
      : document.querySelector("#zone-message")
    this.edit_zip = zipCodeOff ? null : document.querySelector("#edit-zip-btn")
    this.zip_success_input = zipCodeOff
      ? null
      : document.querySelector("#zip-success-input")

    /** @type {PickupZoneObj[]} */
    this.pickupZones = []
    /** @type {DateExceptionObj[]} */
    this.dateExceptions = []

    this.selectedBundleData = null

    /** @type {ZoneObj} */
    this.selectedZone = null
    /** @type {string} */
    this.zipcode = null
    /** @type {string} YYYY-MM-DD */
    this.selectedDate = null
    /** @type {boolean} */
    this.isPickupSelected = false

    /** @type {boolean} */
    this.processingZipcode = false

    /** @type {HTMLElement} */
    this.zoneFormHTML = null

    /** @type {boolean} */
    this.selectedVariantPrice = null

    // Flag for debug logs
    this.ShowDebugLogs = false

    this.init()
  }

  get spinnerSvg() {
    return `<svg fill="none" class="w-10 h-10 animate-spin m-auto" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path clip-rule="evenodd" d="M15.165 8.53a.5.5 0 01-.404.58A7 7 0 1023 16a.5.5 0 011 0 8 8 0 11-9.416-7.874.5.5 0 01.58.404z" fill="currentColor" fill-rule="evenodd" /></svg>`
  }

  get logger() {
    return {
      debug: this.debug.bind(this),
    }
  }

  get isShipping() {
    if (!this.selectedZone) return false
    return (
      this.selectedZone.color === "orange" ||
      this.selectedZone.color === "yellow"
    )
  }

  get isTwoDayShipping() {
    if (!this.selectedZone) return false
    return this.selectedZone.color === "yellow"
  }

  get selectedZoneId() {
    return this.selectedZone?.id || null
  }

  get userTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  get zoneTimeZone() {
    return this.selectedZone?.ShopId === 3
      ? "America/Detroit"
      : "America/Chicago"
  }

  debug(...msg) {
    if (!this.ShowDebugLogs) return
    console.log(...msg)
  }

  /**
   * Init
   */
  async init() {
    this.addEventListeners()
    // if (this.dailies && this.productException) this.checkProductAvailability()

    if (!this.datePickerOff) {
      await this.loadDateExceptions()
    }

    if (this.preselectedData && !this.pickupOff && !this.isSubscription) {
      this.setPreSelectedData()
    }
  }

  formatAvilabilityDates(dateString) {
    const dateArr = dateString.split("-")
    return this.dateAndTime.format(
      new Date(dateArr[0], dateArr[1] - 1, dateArr[2]),
      "M/D",
    )
  }

  isFutureDate(dateString) {
    const todayMs = new Date().getTime()
    const dateMs = new Date(dateString).getTime()
    return dateMs > todayMs ? dateString : null
  }

  checkProductAvailability() {
    const minDate = this.productException.min_date
      ? this.isFutureDate(this.productException.min_date)
      : null
    const maxDate = this.productException.max_date
    const availableDates = this.productException.available_dates
    const productAvailabilityEl = document.getElementById(
      "product-availability",
    )

    if (minDate && maxDate) {
      productAvailabilityEl.innerText = ` between ${this.formatAvilabilityDates(minDate)} and ${this.formatAvilabilityDates(maxDate)}`
    } else if (minDate) {
      const availabilityText = ` starting ${this.formatAvilabilityDates(minDate)}`
      if (!availableDates) productAvailabilityEl.innerText = availabilityText
      else
        productAvailabilityEl.innerText = ` ${availabilityText} and select dates`
    } else if (maxDate) {
      const availabilityText = ` until ${this.formatAvilabilityDates(maxDate)}`
      if (!availableDates) productAvailabilityEl.innerText = availabilityText
      else
        productAvailabilityEl.innerText = ` on select dates ${availabilityText}`
    } else if (availableDates) {
      productAvailabilityEl.innerText = " on select dates"
    }
  }

  /**
   * Setup event listeners for inputs and window click
   */
  addEventListeners() {
    if (!this.pickupOff) {
      // Delivery radio button clicked
      this.delivery_select.addEventListener("click", (e) => {
        if (!this.isPickupSelected) return

        if (
          (this.preselectedData && !this.preselectedData.Pickup) ||
          (!this.preselectedData && this.zoneFormHTML)
        ) {
          this.zone_form.innerHTML = this.zoneFormHTML
        }

        this.selectedZone = null
        this.zip_input.classList.remove("hidden")
        this.pickup_dropdown.classList.replace("flex", "hidden")
        this.isPickupSelected = false
        this.zoneDatePickerEventHandler()
      })
      // Pickup radio button clicked
      this.pickup_select.addEventListener("click", async () => {
        this.zoneFormHTML = this.zone_form.innerHTML
        this.zone_form.innerHTML = this.spinnerSvg

        this.isPickupSelected = true
        this.resetZip()

        if (!this.pickupZones.length) await this.loadPickupLocations()

        this.setZoneMessage()
        this.pickup_dropdown.classList.replace("hidden", "flex")
        this.zip_input.classList.add("hidden")
        this.clearPickups()

        if (this.preselectedData && this.preselectedData.Pickup)
          this.preselectedData.Pickup = false
      })
    }

    if (!this.zipCodeOff) {
      // Submit zip code
      this.zone_form.addEventListener("submit", (e) => {
        e.preventDefault()
        this.submitZipcode(e.target[0].value)
      })
      this.zone_form.addEventListener(
        "blur",
        (e) => {
          e.preventDefault()
          const zip = e.target.value
          if (zip.length === 5) this.submitZipcode(zip)
        },
        true,
      )
      this.edit_zip.addEventListener("click", () => {
        this.resetZip()
      })

      document
        .getElementById("edit-studio-dropdown")
        .addEventListener("click", () => {
          this.clearPickups()
          document.getElementById("studio-list").classList.remove("hidden")
        })
    }

    window.addEventListener("click", (e) => {
      const selectPickupContainer = this.pickup_container
      if (selectPickupContainer) {
        const studioList = document.getElementById("studio-list")
        if (document.getElementById("selected-studio")?.contains(e.target)) {
          // Clicked in box
          selectPickupContainer.classList.add("hidden")
          document.getElementById("studio-height-div").classList.add("h-14")
          studioList.classList.remove("hidden")
        } else {
          selectPickupContainer.classList.remove("hidden")
          document.getElementById("studio-height-div").classList.remove("h-14")
          studioList.classList.add("hidden")
        }
      }
    })

    document.addEventListener("goodpairingselector", (event) => {
      if (!event.detail) return
      this.handleGoodsSelection(event.detail)
    })

    //preselect stand alone datepicker
    document.addEventListener("preselectdate", (e) => {
      if (!e.detail.preselectedDate) this.datePicker.resetDeliveryDate(true)
      else this.datePicker.setSelectedDate(e.detail.preselectedDate)
    })
  }

  async setPreSelectedData() {
    if (this.preselectedData.Pickup) {
      this.isPickupSelected = true
      this.setZoneMessage()
      this.pickup_dropdown.classList.replace("hidden", "flex")
      this.zip_input.classList.add("hidden")
      this.delivery_select.checked = false
      this.pickup_select.checked = true
      await this.loadPickupLocations()
      const pickupId = this.preselectedData.Pickup
      let preselectedCity = "Detroit (1490 Gratiot Ave)"
      if (pickupId === "17") {
        preselectedCity = "Chicago (1812 West Hubbard St)"
      } else if (pickupId === "10") {
        preselectedCity = "Milwaukee (133 West Pittsburgh Ave)"
      }
      this.selectLocation(null, preselectedCity)
    } else if (this.preselectedData["_zipcode"]) {
      this.isPickupSelected = false
      this.submitZipcode(this.preselectedData["_zipcode"])
    }

    if (this.datePickerOff) return

    const deliveryDate = this.preselectedData["Delivery Date"] || "" // YYYY-MM-DD

    // Delivery date not set or unavailable
    if (!deliveryDate || !this.isDateAvailableFunc(deliveryDate)) {
      this.logger.debug("Selected date unavailable", deliveryDate)
      this.datePicker.resetDeliveryDate()
      return
    }

    this.logger.debug("Delivery date is available", deliveryDate)
    this.datePicker.setSelectedDate(deliveryDate)
  }

  selectLocation(e, preselectedCity) {
    const selectedItem = preselectedCity || e.target.innerText
    const city = selectedItem.split(" ")[0]
    const address = selectedItem
      .replace(/[()]/g, "")
      .substr(selectedItem.indexOf(" ") + 1)
    const pickupMessageString = `Pickup at ${address}`
    this.pickup_input.classList.add("hidden")
    this.edit_pickup.classList.remove("hidden")
    this.expand_pickup.classList.add("hidden")
    this.pickup_action.classList.remove(...["right-0", "pointer-events-none"])
    this.pickup_action.classList.add("-right-2")
    this.pickup_message.innerText = pickupMessageString
    this.pickup_container.classList.replace("bg-white", "bg-[#e2e2e2]")

    this.selectedZone = this.pickupZones.find((zone) =>
      zone.name.includes(city),
    )
    if (city === "Chicago") {
      this.zipcode = "60622"
    } else if (city === "Milwaukee") {
      this.zipcode = "53204"
    } else {
      this.zipcode = "48207"
    }

    if (this.zoneTimeZone !== this.userTimeZone) this.updateTimeZone()
    if (!this.datePickerOff) {
      const ineligibleDate = this.selectedDate
        ? !this.isDateAvailableFunc(this.selectedDate)
        : true
      if (ineligibleDate) {
        this.datePicker.resetDeliveryDate(true)
      }

      if (this.zoneTimeZone !== this.userTimeZone) this.updateTimeZone()
      else this.datePicker.refreshCalendarDates()
    }

    this.zoneDatePickerEventHandler()
  }

  async loadPickupLocations() {
    try {
      let response = await fetch(
        "https://admin.f4d.space/api/shopify/pickup-locations",
        this.getFetchConfig("GET"),
      )
      if (response.ok) {
        response = await response.json()
        const locations_list = document.querySelector("#studio-list")
        /** @type {PickupZoneObj[]} */
        let pickupZones = response.reverse()

        // Filter to only show Chicago locations for merch
        if (this.isMerch) {
          pickupZones = pickupZones.filter(zone => 
            zone.location.city.toLowerCase() === 'chicago'
          )
        }

        for (const pickupZone of pickupZones) {
          const listItem = document.createElement("li")
          listItem.classList.add(
            ...[
              "select-none",
              "relative",
              "py-3",
              "px-4",
              "cursor-pointer",
              "hover:bg-gray-100",
              "max-w-full",
              "truncate",
            ],
          )
          listItem.setAttribute("role", "option")
          const cityString = `${pickupZone.location.city} (${pickupZone.location.address1})`
          listItem.innerText = cityString
          listItem.addEventListener("click", (e) => {
            this.selectLocation(e)
          })
          locations_list.append(listItem)
        }

        this.pickupZones = pickupZones
      } else {
        throw new Error("failed to fetch pickup locations")
      }
    } catch (err) {
      console.error("Pickup locations failed", err)
    }
  }

  handleGoodsSelection(goodsSelectorData) {
    /* example input
     {
        goodsItem: Object,
        includePairing: true,
        firstAvailable: '2023-02-10',
        lastAvailable: '2023-02-20',
        unavailableShipping: true, //null
      }
     */

    this.selectedBundleData =
      goodsSelectorData && goodsSelectorData.includePairing
        ? goodsSelectorData
        : null

    this.logger.debug("Selected bundle data=", this.selectedBundleData)

    if (
      this.selectedBundleData &&
      this.selectedDate &&
      this.selectedDate < this.selectedBundleData.firstAvailable
    ) {
      this.datePicker.resetDeliveryDate(true)
    }
    if (this.datePicker) this.datePicker.refreshCalendarDates()
  }

  async loadDateExceptions() {
    const dateExceptionDate = this.dateAndTime
      .addDays(new Date(), -2)
      .toISOString()
      .split("T")[0]

    let path = `https://admin.f4d.space/api/shopify/date-exceptions?from=${dateExceptionDate}`

    try {
      let response = await fetch(path, this.getFetchConfig("GET"))

      if (response.ok) {
        response = await response.json()
      } else {
        throw new Error("failed to fetch date exceptions")
      }
      if (response.dateExceptions) {
        this.dateExceptions = response.dateExceptions

        this.logger.debug("Loaded dateExceptions=", this.dateExceptions)
      } else {
        // Keep support for old payload to not break transition
        this.dateExceptions = response
      }

      if (
        (this.productException || this.dateExceptions.length) &&
        this.datePicker
      ) {
        this.datePicker.refreshCalendarDates()
      }
    } catch (error) {
      console.error("Load date exceptions failed", error)
    }
  }

  dateSelectedFunc(dateString) {
    this.selectedDate = dateString
    this.zoneDatePickerEventHandler()
  }

  isDateAvailableFunc(dateString) {
    if (!dateString) {
      console.error("isDateAvailableFunc: Invalid dateString", dateString)
      return false
    }

    const dateStringSplit = dateString.split("-")
    const selectedMonth = Number(dateStringSplit[1])
    const selectedDay = Number(dateStringSplit[2])
    const jsDate = new Date(dateStringSplit[0], selectedMonth - 1, selectedDay)
    const dayOfWeek = jsDate.getDay() // Sun = 0, Mon = 1, ... Sat = 6

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()

    const datediff = (first, second) =>
      Math.round((second - first) / (1000 * 60 * 60 * 24))
    const daysFromToday = datediff(
      new Date(currentYear, currentMonth, new Date().getDate()),
      jsDate,
    )
    const isToday = daysFromToday === 0

    this.logger.debug(
      "Checking if date available",
      dateString,
      "dayOfWeek=",
      dayOfWeek,
      "DaysFromToday=",
      daysFromToday,
      "isToday?",
      isToday,
    )

    if (daysFromToday < 0) {
      return false
    }

    if (this.isSubscription) {
      if (daysFromToday < 3) {
        return false
      } 
    }

    if (this.isWedding || this.isEvent) {
      if (this.selfServeExceptions.includes(dateString)) return false
      if (this.isEvent) {
        if (daysFromToday < 4) return false
      } else {
        if (daysFromToday < 7) return false
      }

      if (this.selectedZone && this.selfServeExceptionsByShop[String(this.selectedZone.ShopId)]?.includes(dateString)) {
        return false
      }
    }

    // Product Exception
    if (this.productException) {
      // check for available dates array first
      if (this.productException.available_dates) {
        if (!this.productException.available_dates.includes(dateString))
          return false
      } else {
        // otherwise use min / max date
        if (
          this.productException.min_date &&
          dateString < this.productException.min_date
        )
          return false
        if (
          this.productException.max_date &&
          dateString > this.productException.max_date
        )
          return false
      }
    }

    if (!this.selectedZone) {
      // If no zone then default to disable Sundays
      // return dayOfWeek !== 0

      // Default to show all dates for the time being
      return true
    }

    // If there is a bundle selected then check firstAvailable / lastAvailable
    if (this.selectedBundleData) {
      if (
        this.selectedBundleData.firstAvailable &&
        dateString < this.selectedBundleData.firstAvailable
      ) {
        return false
      }
      if (
        this.selectedBundleData.lastAvailable &&
        dateString > this.selectedBundleData.lastAvailable
      ) {
        return false
      }
    }

    // no shipping same days and no shipping next day for 2 day shipping
    if (this.isTwoDayShipping && daysFromToday < 2) {
      return false
    }
    if (this.isShipping && daysFromToday < 1) {
      return false
    }

    // For shipping "same day" would be the day before the selected delivery day
    //  e.g. if today is 2023-02-08 and I'm shipping, then 2023-02-09 would be considered "same day"
    const isSameDay = this.isTwoDayShipping
      ? daysFromToday === 2
      : this.isShipping
        ? daysFromToday === 1
        : isToday

    // Date exception
    const dateException = this.dateExceptions.find(
      (de) => de.date == dateString && de.ZoneId == this.selectedZoneId,
    )

    if (dateException) {
      if (!dateException.isAvailable) return false
      if (isSameDay) {
        if (!dateException.isAvailableSameDay) return false
        else if (
          dateException.sameDayCutOff <=
          this.getCurrentHourInTimezone(this.zoneTimeZone)
        )
          return false
      }

      return true
    }

    // Regular zone week day
    const zoneDayOfWeek = ((dayOfWeek + 6) % 7) + 1 // Mon = 1, Tues = 2, ..., Sun = 7
    const zoneWeekDay = this.selectedZone.ZoneWeekDays.find(
      (z) => z.weekday == zoneDayOfWeek,
    )
    if (zoneWeekDay) {
      if (!zoneWeekDay.isAvailable) return false
      if (isSameDay) {
        if (!zoneWeekDay.isAvailableSameDay) return false
        else if (
          zoneWeekDay.sameDayCutOff <=
          this.getCurrentHourInTimezone(this.zoneTimeZone)
        )
          return false
      }

      return true
    }

    return true
  }

  highlightDateFunc(dateString) {
    return false
    // Thanksgiving eve
    // return dateString === "2024-11-27"
    // return ['2024-02-14'].includes(dateString) && this.isShipping
    // return ['2023-05-12', '2023-05-13', '2023-05-14'].includes(dateString) && this.isShipping
  }

  getCurrentHourInTimezone(timeZone) {
    const dt = new Date()
    return parseInt(
      dt.toLocaleString("en-US", {
        timeZone,
        hour: "numeric",
        hour12: false,
      }),
    )
  }

  updateTimeZone() {
    if (!this.datePicker) return
    this.datePicker.updateTimeZone()
  }

  submitZipcode(zipcode) {
    if (this.processingZipcode) return
    if (!zipcode || zipcode.length !== 5) {
      console.error("Invalid zip code", zipcode)
      this.setZoneMessage("Invalid Zip Code", true)
      return
    }

    this.processingZipcode = true
    const zoneForm = this.zone_form.innerHTML
    this.zone_form.innerHTML = this.spinnerSvg

    const config = this.getFetchConfig("POST", JSON.stringify({ zipcode }))
    return fetch("https://admin.f4d.space/api/shopify/zipcode-zone", config)
      .then((response) => {
        if (response.status === 200) return response.json()
        else throw new Error(response.status)
      })
      .then((response) => {
        this.zone_form.innerHTML = zoneForm
        this.zone_form[0].value = zipcode
        if (response.isActive) {
          let zipcodeAvailable = true
          this.selectedZone = response.Zone
          this.zipcode = zipcode
          document.getElementById("selected-zip").innerText = zipcode
          if (this.deliveryMap) {
            this.edit_zip.classList.replace("text-base", "text-xl")
            this.setZoneMessage("", false, [
              "desktop:text-2xl",
              "font-barlow",
              "text-xl",
            ])
            if (["green", "blue"].includes(this.selectedZone.color))
              this.zipCodeSuccess(
                `Huzzah! This zip code includes $${this.selectedZone.deliveryCharge}.00 next-day delivery and $${this.selectedZone.sameDayDeliveryCharge}.00 same-day delivery, as well as Weddings and Events.`,
              )
            else if (this.isTwoDayShipping) {
              //  throw new Error("We currently don't ship to your area.")
              this.zipCodeSuccess(
                `Huzzah! This zip code is in our shipping area & includes a $${this.selectedZone.deliveryCharge}.00 delivery fee.`,
              )
            } else if (this.isShipping)
              this.zipCodeSuccess(
                `Huzzah! This zip code is in our overnight shipping area & includes a $${this.selectedZone.deliveryCharge}.00 delivery fee.`,
              )
            else
              this.zipCodeSuccess(
                `Huzzah! This zip code includes a $${this.selectedZone.deliveryCharge}.00 delivery fee (but not yet same-day delivery), as well as Weddings and Events.`,
              )
          } else if (this.isSubscription) {
            this.zipCodeSuccess(
              `${this.isShipping ? "Shipping" : "Delivery"} to ${zipcode} is $${this.selectedZone.deliveryCharge} per delivery.`,
            )
          } else if (this.isWedding || this.isEvent) {
            if (this.isShipping) {
              this.setZoneMessage(
                this.isWedding
                  ? "Sorry, we cannot deliver wedding flowers to this zip code."
                  : "Sorry, we cannot deliver event flowers to this zip code.",
                true,
              )
              this.zone_form.innerHTML = zoneForm
              this.selectedZone = null
              return
            } else {
              let minimumPriceDescription = ""
              if (this.selectedZone.ShopId === 1) {
                if (["green", "blue"].includes(this.selectedZone.color)) {
                  minimumPriceDescription = " (minimum $50)"
                } else if (this.selectedZone.color === "red") {
                  minimumPriceDescription = " (minimum $75)"
                }
              }
              this.zipCodeSuccess(
                `Delivery to ${zipcode} is +10%${minimumPriceDescription}`,
              )
            }
          } else {
            if (this.isMerch) {
              this.zipCodeSuccess(
                "Shipping via UPS is $10."
              )
            } else if (["green", "blue"].includes(this.selectedZone.color)) {
              this.zipCodeSuccess(
                `Local delivery $${this.selectedZone.deliveryCharge} and up. Same day $${this.selectedZone.sameDayDeliveryCharge}.`,
                "Delivery",
              )
            } else {
              //if (this.selectedZone.color === 'yellow') throw new Error("We don't deliver to that zip!")
              this.zipCodeSuccess(
                `${this.isShipping ? `Shipping via ${this.isTwoDayShipping ? "FedEx" : "UPS"} is ` : "Local delivery "}$${this.selectedZone.deliveryCharge}.`,
                this.isShipping ? "Shipping" : "Delivery",
              )
            }
          }

          if (zipcodeAvailable) {
            if (!this.datePickerOff) {
              const ineligibleDate = this.selectedDate
                ? !this.isDateAvailableFunc(this.selectedDate)
                : true
              if (ineligibleDate) {
                this.datePicker.resetDeliveryDate(true)
              }

              if (this.zoneTimeZone !== this.userTimeZone) this.updateTimeZone()
              else this.datePicker.refreshCalendarDates()
            }
          }
        } else {
          this.selectedZone = response.Zone
          throw new Error("We don't deliver to that zip!")
        }
      })
      .catch((error) => {
        if (zipcode.length === 5) {
          if (error.message === "404") {
            if (this.deliveryMap) {
              this.edit_zip.classList.replace("text-base", "text-xl")
              this.setZoneMessage(
                "We do not service that area at this time. However, you may be eligible for a Wedding or Event.",
                false,
                ["text-2xl", "font-barlow"],
              )
              this.zip_input.classList.add("hidden")
              this.zip_success_input.classList.remove("hidden")
            } else {
              if (this.isWedding) {
                this.setZoneMessage(
                  "Sorry, we cannot deliver wedding flowers to this zip code.",
                  true,
                )
              } else if (this.isEvent) {
                this.setZoneMessage(
                  "Sorry, we cannot deliver event flowers to this zip code.",
                  true,
                )
              } else {
                this.setZoneMessage("We don't deliver to that zip!", true)
              }
            }
          } else {
            if (this.selectedZone.color === "yellow")
              this.setZoneMessage(error.message, true)
            else
              this.setZoneMessage(
                "Something went wrong, please try again later",
                true,
              )
          }
        } else {
          this.setZoneMessage("Invalid Zip", true)
        }
        this.zone_form.innerHTML = zoneForm

        this.selectedZone = null
        console.error(`Zip check failed: ${error.message} error`)
      })
      .finally(() => {
        this.processingZipcode = false
        this.zoneDatePickerEventHandler()
      })
  }

  zipCodeSuccess(message = "", modeOfTransport) {
    if (this.freeShippingDeal) {
      this.setZoneMessage(
        message + ` Free ${modeOfTransport.toLowerCase()} on orders over $90!`,
      )
    } else {
      this.setZoneMessage(message)
    }

    this.zip_input.classList.add("hidden")
    this.zip_success_input.classList.remove("hidden")
  }

  setZoneMessage(message = "", error, addClasses = []) {
    if (error) this.zone_message.classList.add("text-red-500")
    else this.zone_message.classList.remove("text-red-500")

    this.zone_message.classList.add(...addClasses)
    this.zone_message.innerText = message
  }

  /**
   *
   * Special message for holiday price changes
   */
  setSpecialDeliveryDateMessage() {
    if (!this.datePicker) return

    const todayDateString = new Date().toISOString().split("T")[0]
    const isSameDay = todayDateString === this.selectedDate

    let specialMessage = ""
    if (this.selectedZone) {
      // Thanksgiving eve price exception. price only changing for green, blue, red and orange zones
      if (this.selectedDate === "2024-11-27") {
        let specialPrice = 12
        if (this.isShipping) {
          specialPrice = this.isTwoDayShipping ? 25 : 20
          specialMessage = `Thanksgiving Eve shipping is $${specialPrice}.00.`
        } else {
          specialPrice =
            this.selectedZone.color === "red" ||
            this.selectedZone.color === "brown"
              ? 15
              : 12
          specialMessage = `Thanksgiving Eve delivery is $${specialPrice}.00.`
        }
      } else if (this.selectedDate === "2025-02-14") {
        if (this.isShipping) {
          const specialPrice = this.isTwoDayShipping ? 25 : 20
          specialMessage = `Valentine's Day shipping is $${specialPrice}.00.`
        } else {
          let specialPrice = 16
          if (this.selectedZone.color === "red") {
            specialPrice = 22
          } else if (this.selectedZone.color === "brown") {
            specialPrice = 25
          }
          specialMessage = `Valentine's Day delivery is $${specialPrice}.00.`
        }
      } else if (["2025-05-10", "2025-05-11"].includes(this.selectedDate)) {
        // Yellow zone $25, Orange zone $20
        if (this.isShipping) {
          specialMessage = `Mother's Day weekend shipping is $${this.isTwoDayShipping ? 25 : 20}.00 and will arrive by Sunday 5/11`
        } else if (!this.isPickupSelected) {
          // Saturday: Green zone $12, Blue/Red/Brown zone $15
          // Sunday: Green zone $15, Blue/Red/Brown zone $18
          let specialPrice = this.selectedZone.color === "green" ? 12 : 15
          if (this.selectedDate === "2025-05-11" && this.selectedZone.ShopId === 1) {
            specialPrice = this.selectedZone.color === "green" ? 15 : 18
          }
          
          if (isSameDay) {
            if (this.selectedZone.color === "green") {
              specialPrice = 20
            } else if (this.selectedZone.color === "blue" || this.selectedZone.color === "red") {
              specialPrice = 25
            }
          }

          if (this.selectedDate === "2025-05-11") {
            specialMessage = `Mother's Day Sunday delivery is $${specialPrice}.00.`
          } else {
            specialMessage = `Saturday delivery is $${specialPrice}.00.`
          }
        }
      } else if (this.selectedDate === "2025-05-09" && this.isShipping) {
        specialMessage = `Mother's Day weekend shipping is $${this.isTwoDayShipping ? 25 : 18}.00 and will arrive by Sunday 5/11`
      }
    }

    const container = document.getElementById("date-special-message")
    if (container) {
      container.innerHTML = specialMessage ? `<p>${specialMessage}</p>` : ""
    }

    //Toggle regular zone message based on special message
    if (specialMessage) this.zone_message?.classList.add("hidden")
    else this.zone_message?.classList.remove("hidden")
  }

  resetZip() {
    this.selectedZone = null
    this.zipcode = null
    this.setZoneMessage()
    this.zip_input.classList.remove("hidden")
    this.zip_success_input.classList.add("hidden")
    this.zoneDatePickerEventHandler()
    if (this.datePicker) this.datePicker.refreshCalendarDates()
  }

  clearPickups() {
    this.selectedZone = null
    this.pickup_container.classList.replace("bg-[#e2e2e2]", "bg-white")
    this.pickup_input.classList.remove("hidden")
    this.edit_pickup.classList.add("hidden")
    this.expand_pickup.classList.remove("hidden")
    this.pickup_message.innerText = ""
    this.pickup_action.classList.add(...["right-0", "pointer-events-none"])
    this.pickup_action.classList.remove("-right-2")
    this.zoneDatePickerEventHandler()
    if (this.datePicker) this.datePicker.refreshCalendarDates()
  }

  zoneDatePickerEventHandler() {
    this.setSpecialDeliveryDateMessage()

    const zoneDatePicked = new CustomEvent("zonedatepicked", {
      detail: {
        zoneAvailable: !!this.selectedZone,
        dateAvailable: !!this.selectedDate,
        zone: this.selectedZone,
        deliveryDate: this.selectedDate,
        zipcode: this.zipcode,
      },
    })
    document.dispatchEvent(zoneDatePicked)
  }

  getFetchConfig(method, body = null) {
    const config = {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
        Authorization:
          "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjoiY2xpZW50IiwiZXhwIjoxNzI2ODQ5MjI1NTM3LCJpYXQiOjE2NjM3NzcyMjV9.3j5DSp1wxF2hi6rgYckXfgvppXgA-9bQn2aUfZKYUDg",
      },
      mode: "cors",
    }
    if (body) config.body = body
    return config
  }
}
